import { MarketplaceItem } from '@/apis/marketplace/marketplace.schema'
import { Button } from '@/app/common/components/Button'
import { Input } from '@/app/common/components/Input'
import { BackIcon, CartIcon, MagnifierIcon } from '@/app/common/icons/general'
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
} from '@/common/components/Card.tsx'
import Pagination from '@/common/components/Pagination.tsx'
import SimpleSelector, { ListItem } from '@/common/components/SimpleSelector'
import { TableToolBar } from '@/common/components/StackedTable.tsx'
import { FilterIcon } from '@/common/icons/general.tsx'
import {
  MarketplaceAffiliation,
  MarketplaceStore,
} from '@/queries/marketplace/schema.ts'
import AddStudentButton from '@/app/common/components/AddStudentButton/AddStudentButton.tsx'
import { EllipsesLoader } from '@/common/components/EllipsesLoader.tsx'
import { StoreScope } from '@/app/pages/marketplace/utils'
import { useTranslation } from 'react-i18next'
import Cart from '../components/Cart'
import StoreCard from './StoreCard'
import StoreScopeToggle from './StoreScopeToggle'
import AffiliationCard from './AffiliationCard'
import CardItem from './CardItem'

type MarketplaceView = 'stores' | 'items' | 'affiliations'

interface Props {
  // Data
  stores: MarketplaceStore[]
  affiliations: MarketplaceAffiliation[]
  items: MarketplaceItem[]
  totalRecords: number

  // UI State
  showCart: boolean
  search: string
  currentView: MarketplaceView
  showAffiliationsView?: boolean
  isLoading?: boolean

  // Store scope ("My Schools" / "All Storefronts")
  storeScope?: StoreScope
  showStoreScopeToggle?: boolean
  showAddStudentPrompt?: boolean
  onStoreScopeChange?: (scope: StoreScope) => void

  // Pagination
  currentPage: number
  perPage: number

  // Filters
  filters: ListItem[]
  availabilityFilters?: ListItem[]

  // Actions
  onSearchChange: (search: string) => void
  onViewChange: (view: MarketplaceView) => void
  onFilterChange: (item: ListItem) => void
  onItemClick: (item: MarketplaceItem) => void
  onAffiliationClick: (affiliation: MarketplaceAffiliation) => void
  onProviderClick: (providerId: number) => void
  onPageChange: (page: number) => void
  onPerPageChange: (perPage: number) => void
  hideCart: () => void
  handleStoreClick: (store: MarketplaceStore) => void
  onAvailabilityFilterChange?: (item: ListItem) => void
}

export default function MarketplaceUI({
  stores,
  affiliations,
  items,
  totalRecords,
  showCart,
  search,
  currentView,
  currentPage,
  perPage,
  showAffiliationsView = true,
  isLoading = false,

  storeScope,
  showStoreScopeToggle = false,
  showAddStudentPrompt = false,
  onStoreScopeChange,

  filters,
  availabilityFilters,

  // Actions
  onSearchChange,
  onViewChange,
  onFilterChange,
  onAvailabilityFilterChange,
  onItemClick,
  onAffiliationClick,
  onProviderClick,
  onPageChange,
  onPerPageChange,
  handleStoreClick,
  hideCart,
}: Props) {
  const { t: translate } = useTranslation()

  const isStoresView = currentView === 'stores'
  const isItemsView = currentView === 'items'
  const isAffiliationsView = currentView === 'affiliations'
  const isMySchoolsScope =
    isStoresView && showStoreScopeToggle && storeScope === 'my_schools'

  return (
    <Card className="overflow-visible relative z-10">
      <CardHeader className="flex justify-between items-center text-md font-semibold">
        <div className="flex flex-wrap items-center gap-3 text-left text-md font-semibold text-gray-900">
          <span>
            {isStoresView
              ? 'Stores'
              : isAffiliationsView
                ? 'Affiliations'
                : translate('items')}
          </span>
          {isStoresView &&
            !showCart &&
            showStoreScopeToggle &&
            storeScope &&
            onStoreScopeChange && (
              <StoreScopeToggle
                value={storeScope}
                onChange={onStoreScopeChange}
              />
            )}
        </div>
        {!showCart && (
          <TableToolBar>
            {showAffiliationsView && (
              <div className="rounded-md shadow-sm lg-sm:flex-none">
                <Button
                  variant={isAffiliationsView ? 'primary' : 'outline'}
                  size="md"
                  onClick={() => onViewChange('affiliations')}
                  className="w-full whitespace-nowrap"
                >
                  Show Affiliations
                </Button>
              </div>
            )}

            <div className="rounded-md shadow-sm lg-sm:flex-none lg-sm:ps-4">
              <Button
                variant={isItemsView ? 'primary' : 'outline'}
                size="md"
                onClick={() => onViewChange('items')}
                className="w-full whitespace-nowrap"
              >
                Show Items
              </Button>
            </div>

            <div className="rounded-md shadow-sm lg-sm:flex-none lg-sm:ps-4">
              <Button
                variant={isStoresView ? 'primary' : 'outline'}
                size="md"
                onClick={() => onViewChange('stores')}
                className="w-full whitespace-nowrap"
              >
                Show Stores
              </Button>
            </div>

            <div className="relative rounded-md shadow-sm flex-1 lg-sm:ps-4">
              <Input
                leftIcon={<MagnifierIcon />}
                className="shadow-none w-full"
                placeholder={
                  isAffiliationsView
                    ? 'Search by Affiliation Name'
                    : 'Search by Name or Type'
                }
                value={search}
                onChange={(e) => onSearchChange(e.target.value)}
              />
            </div>

            {!isAffiliationsView && (
              <div
                key={`business-type-filter-${currentView}`}
                className="relative rounded-md shadow-sm flex-1"
              >
                <SimpleSelector
                  list={filters}
                  onChange={onFilterChange}
                  className="pt-4 lg-sm:ps-4 lg-sm:pt-0 w-full"
                  icon={<FilterIcon className="text-primary" />}
                />
              </div>
            )}
            {availabilityFilters && onAvailabilityFilterChange && (
              <div
                key="availability-filter"
                className="relative w-full rounded-md shadow-sm lg-sm:w-56 lg-sm:flex-none"
              >
                <SimpleSelector
                  list={availabilityFilters}
                  onChange={onAvailabilityFilterChange}
                  className="pt-4 lg-sm:ps-4 lg-sm:pt-0 w-full"
                  icon={<FilterIcon className="text-primary" />}
                />
              </div>
            )}
          </TableToolBar>
        )}
      </CardHeader>

      <CardContent>
        <div className="flex flex-col">
          {showCart ? (
            <div>
              <div className="flex justify-between items-center mb-6">
                <div className="flex items-center gap-2">
                  <button
                    onClick={hideCart}
                    className="p-2 hover:bg-gray-100 rounded-full"
                  >
                    <BackIcon />
                  </button>
                  <CartIcon className="w-10 h-10" />
                  <div>
                    <h1 className="text-xl font-bold text-gray-900">Cart</h1>
                  </div>
                </div>
              </div>

              <Cart hideCart={hideCart} />
            </div>
          ) : (
            <>
              {isStoresView && showAddStudentPrompt && (
                <div className="mb-6 flex flex-col gap-3 rounded-md border border-primary-100 bg-primary-50 p-4 sm:flex-row sm:items-center sm:justify-between">
                  <p className="text-sm text-gray-700">
                    {translate('marketplace_add_student_prompt')}
                  </p>
                  <AddStudentButton />
                </div>
              )}

              {isLoading ? (
                <div role="status" className="flex justify-center py-10">
                  <EllipsesLoader size="md" />
                  <span className="sr-only">{translate('loading')}</span>
                </div>
              ) : isStoresView ? (
                stores && stores.length > 0 ? (
                  <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4 xl:gap-6">
                    {stores.map((store, index) => (
                      <StoreCard
                        handleStoreClick={handleStoreClick}
                        key={index}
                        store={store}
                      />
                    ))}
                  </div>
                ) : isMySchoolsScope && onStoreScopeChange ? (
                  <div className="flex flex-col items-center gap-4 text-center text-gray-500 py-10">
                    <p>{translate('no_my_schools_stores')}</p>
                    <Button
                      variant="outline"
                      size="md"
                      onClick={() => onStoreScopeChange('all')}
                    >
                      {translate('browse_all_storefronts')}
                    </Button>
                  </div>
                ) : (
                  <div className="text-center text-gray-500 py-10">
                    No stores were found.
                  </div>
                )
              ) : isAffiliationsView ? (
                affiliations && affiliations.length > 0 ? (
                  <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4 xl:gap-6">
                    {affiliations.map((affiliation) => (
                      <AffiliationCard
                        key={affiliation.id}
                        affiliation={affiliation}
                        onClick={onAffiliationClick}
                      />
                    ))}
                  </div>
                ) : (
                  <div className="text-center text-gray-500 py-10">
                    No affiliations were found.
                  </div>
                )
              ) : items && items.length > 0 ? (
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3 xl:gap-6">
                  {items.map((item) => (
                    <CardItem
                      key={item.id}
                      item={item}
                      provider={item.provider}
                      onItemClick={onItemClick}
                      onProviderClick={onProviderClick}
                    />
                  ))}
                </div>
              ) : (
                <div className="text-center text-gray-500 py-10">
                  No marketplace items were found.
                </div>
              )}
            </>
          )}
        </div>
      </CardContent>

      <CardFooter className="p-0 border-t border-transparent mt-5">
        {!showCart && (
          <Pagination
            itemsPerPage={perPage}
            currentPage={currentPage}
            totalRows={totalRecords}
            onPageChange={onPageChange}
            onPerPageChange={onPerPageChange}
          />
        )}
      </CardFooter>
    </Card>
  )
}
